import { TriangleAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { toUiError } from '../lib/errors.ts';
import type { UiError } from '../lib/errors.ts';
import { Button } from './Button.tsx';
import { Dialog } from './Dialog.tsx';
import { Input } from './Input.tsx';
import { RISK_HINT, RiskBadge } from './RiskBadge.tsx';
import type { RiskLevel } from './RiskBadge.tsx';

export interface StepUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Names the exact action and target: `Stop 3 sessions on build-01?` */
  title: ReactNode;
  /** What happens, including the blast radius. */
  description: ReactNode;
  risk: RiskLevel;
  /** Every target, listed (the dialog shows the first few and a count). */
  scope: string[];
  /** Typed to unlock the button (the target's name, or a count phrase). Omit for a plain confirm. */
  phrase?: string;
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
}

const SHOW = 6;

/**
 * The confirm and step-up step for high-risk actions, from a button, a bulk bar or the
 * command palette alike. Typing the target's name proves intent; the hub still applies
 * policy to the action itself, so this step can only add friction, never remove it.
 */
export function StepUpDialog({ open, onOpenChange, title, description, risk, scope, phrase, confirmLabel, onConfirm }: StepUpDialogProps) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      setTyped('');
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const ready = !phrase || typed.trim().toLowerCase() === phrase.trim().toLowerCase();
  const confirm = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      dismissible={!busy}
      initialFocus={() => (phrase ? inputRef.current : cancelRef.current)}
      footer={
        <>
          <Button ref={cancelRef} variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" loading={busy} disabled={!ready} onClick={() => void confirm()}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-ui text-fg-muted">
          <RiskBadge risk={risk} />
          <span>{RISK_HINT[risk]}</span>
        </div>
        {scope.length > 0 && (
          <div className="rounded-card border border-border bg-sunken px-3 py-2">
            <p className="text-caption font-medium text-fg-subtle">Affects {scope.length === 1 ? 'one target' : `${scope.length} targets`}</p>
            <ul className="mt-1 flex flex-col gap-0.5 text-ui text-fg">
              {scope.slice(0, SHOW).map((s, i) => (
                <li key={`${s}-${i}`} className="truncate">
                  {s}
                </li>
              ))}
              {scope.length > SHOW && <li className="text-fg-subtle">and {scope.length - SHOW} more</li>}
            </ul>
          </div>
        )}
        {phrase && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void confirm();
            }}
          >
            <Input
              ref={inputRef}
              label={
                <span>
                  Type <span className="font-mono text-fg">{phrase}</span> to confirm
                </span>
              }
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </form>
        )}
        {error && (
          <div role="alert" className="flex gap-2 rounded-card border border-destructive-line bg-destructive-soft p-3 text-ui">
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-status-danger" aria-hidden />
            <div className="min-w-0">
              <p className="font-medium text-destructive-ink">{error.what}</p>
              <p className="mt-0.5 text-fg-muted">{error.why}</p>
              <p className="mt-0.5 text-fg-muted">{error.next}</p>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
